import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ChevronRight, Plus, Package, Upload, Paperclip, X, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { can } from "@/lib/permissions";
import { todayIST } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONDITIONS = ["Good", "Damaged", "Short"];
const DOC_TYPES = [
  { value: "eway_bill", label: "E-Way Bill" },
  { value: "invoice", label: "Vendor Invoice" },
  { value: "lr_copy", label: "LR / Lorry Receipt" },
  { value: "other", label: "Other" }
];

export default function MRNPage() {
  const { id: projectId } = useParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [mrns, setMrns] = useState([]);
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadMrn, setUploadMrn] = useState(null);
  const [uploadDocType, setUploadDocType] = useState("eway_bill");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    date: todayIST(),
    vendor: "", material_description: "", po_id: "",
    ordered_quantity: "", received_quantity: "", accepted_quantity: "",
    rejected_quantity: 0, rejection_reason: "", condition: "Good", notes: ""
  });

  useEffect(() => {
    const fetch = async () => {
      try {
        const [pRes, mRes, poRes] = await Promise.all([
          axios.get(`${API}/projects/${projectId}`),
          axios.get(`${API}/mrn?project_id=${projectId}`),
          // POs are optional context — field engineers / sales can log MRNs without po.view
          can(user, "po.view") ? axios.get(`${API}/financials/po?project_id=${projectId}`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        ]);
        setProject(pRes.data);
        setMrns(mRes.data);
        setPos(poRes.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetch();
  }, [projectId]);

  const saveMRN = async (e) => {
    e.preventDefault();
    if (!form.vendor || !form.material_description || !form.received_quantity) {
      toast.error("Please fill required fields");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/mrn`, {
        project_id: projectId,
        ...form,
        ordered_quantity: parseFloat(form.ordered_quantity) || 0,
        received_quantity: parseFloat(form.received_quantity),
        accepted_quantity: parseFloat(form.accepted_quantity) || parseFloat(form.received_quantity),
        rejected_quantity: parseFloat(form.rejected_quantity) || 0,
      });
      setMrns(p => [data, ...p]);
      setShowCreate(false);
      setForm({ date: todayIST(), vendor: "", material_description: "", po_id: "", ordered_quantity: "", received_quantity: "", accepted_quantity: "", rejected_quantity: 0, rejection_reason: "", condition: "Good", notes: "" });
      toast.success("MRN recorded!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save MRN");
    }
    setSaving(false);
  };

  const uploadDocument = async (file) => {
    if (!file || !uploadMrn) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("doc_type", uploadDocType);
      await axios.post(`${API}/mrn/${uploadMrn.id}/upload-document`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`${DOC_TYPES.find(d => d.value === uploadDocType)?.label} uploaded and saved to Document Vault!`);
      // Refresh MRN list to update doc count
      const { data } = await axios.get(`${API}/mrn?project_id=${projectId}`);
      setMrns(data);
      setUploadMrn(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  const conditionColors = { Good: "bg-emerald-100 text-emerald-700", Damaged: "bg-red-100 text-red-700", Short: "bg-orange-100 text-orange-700" };

  if (loading) return <div className="p-8 text-[#64748B]">Loading MRNs...</div>;

  return (
    <div className="p-6 animate-fade-up">
      <div className="flex items-center gap-2 text-xs text-[#64748B] mb-4">
        <Link to="/app/projects" className="hover:text-[#FFB703]">Projects</Link>
        <ChevronRight size={12} />
        <Link to={`/app/projects/${projectId}`} className="hover:text-[#FFB703]">{project?.name}</Link>
        <ChevronRight size={12} />
        <span className="text-[#0A1128] font-medium">Material Receipt Notes</span>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading font-bold text-xl text-[#0A1128]">Material Receipt Notes (MRN)</h1>
        {can(user, "mrn.edit") && <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 bg-[#FFB703] text-[#0A1128] font-semibold px-4 py-2 rounded text-sm hover:bg-[#E8A200] transition-colors active:scale-[0.98]" data-testid="create-mrn-btn">
          <Plus size={14} />Record MRN
        </button>}
      </div>

      {mrns.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center bg-white border border-[#E2E8F0] rounded">
          <Package size={32} className="text-[#94A3B8] mb-3" />
          <p className="text-sm text-[#64748B]">No material deliveries recorded yet</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E2E8F0] rounded overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                  {["MRN #", "Date", "Vendor", "Material", "Ordered", "Received", "Accepted", "Condition", "PO Ref", "Docs", ""].map(h => (
                    <th key={h} className="text-left px-4 py-2.5 text-[10px] font-semibold text-[#64748B] uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mrns.map(m => (
                  <tr key={m.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`mrn-${m.id}`}>
                    <td className="px-4 py-3 text-xs font-semibold text-[#0A1128]">{m.mrn_number}</td>
                    <td className="px-4 py-3 text-xs text-[#64748B]">{m.date}</td>
                    <td className="px-4 py-3 text-xs text-[#0A1128]">{m.vendor}</td>
                    <td className="px-4 py-3 text-xs text-[#0A1128] max-w-40 truncate" title={m.material_description}>{m.material_description}</td>
                    <td className="px-4 py-3 text-xs tabular-nums">{m.ordered_quantity}</td>
                    <td className="px-4 py-3 text-xs tabular-nums">{m.received_quantity}</td>
                    <td className="px-4 py-3 text-xs tabular-nums text-emerald-600 font-medium">{m.accepted_quantity}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${conditionColors[m.condition] || "bg-slate-100 text-slate-600"}`}>{m.condition}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#64748B]">{pos.find(p => p.id === m.po_id)?.po_number || "—"}</td>
                    <td className="px-4 py-3 text-xs">
                      {m.attached_document_ids?.length > 0 ? (
                        <span className="flex items-center gap-1 text-[#64748B]">
                          <Paperclip size={10} />{m.attached_document_ids.length}
                        </span>
                      ) : <span className="text-[#94A3B8]">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => setUploadMrn(m)} title="Attach documents"
                        className="text-[#94A3B8] hover:text-[#FFB703] transition-colors" data-testid={`upload-mrn-doc-${m.id}`}>
                        <Upload size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-[#0A1128]">Record Material Receipt</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveMRN} className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-[#64748B] mb-1">Date *</label>
                <input type="date" required value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
              <div>
                <label className="block text-xs text-[#64748B] mb-1">Link to PO</label>
                <select value={form.po_id} onChange={e => setForm(p => ({ ...p, po_id: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] bg-white">
                  <option value="">-- None --</option>
                  {pos.map(po => <option key={po.id} value={po.id}>{po.po_number} - {po.vendor_name}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-[#64748B] mb-1">Vendor *</label>
                <input type="text" required value={form.vendor} onChange={e => setForm(p => ({ ...p, vendor: e.target.value }))} placeholder="Waaree Energies" className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-[#64748B] mb-1">Material Description *</label>
                <input type="text" required value={form.material_description} onChange={e => setForm(p => ({ ...p, material_description: e.target.value }))} placeholder="Solar Panels 540W Mono PERC" className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
              <div>
                <label className="block text-xs text-[#64748B] mb-1">Ordered Qty</label>
                <input type="number" value={form.ordered_quantity} onChange={e => setForm(p => ({ ...p, ordered_quantity: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
              <div>
                <label className="block text-xs text-[#64748B] mb-1">Received Qty *</label>
                <input type="number" required value={form.received_quantity} onChange={e => setForm(p => ({ ...p, received_quantity: e.target.value, accepted_quantity: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
              <div>
                <label className="block text-xs text-[#64748B] mb-1">Accepted Qty</label>
                <input type="number" value={form.accepted_quantity} onChange={e => setForm(p => ({ ...p, accepted_quantity: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
              <div>
                <label className="block text-xs text-[#64748B] mb-1">Rejected Qty</label>
                <input type="number" value={form.rejected_quantity} onChange={e => setForm(p => ({ ...p, rejected_quantity: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-[#64748B] mb-1">Material Condition</label>
                <select value={form.condition} onChange={e => setForm(p => ({ ...p, condition: e.target.value }))} className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] bg-white">
                  {CONDITIONS.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              {form.rejected_quantity > 0 && (
                <div className="col-span-2">
                  <label className="block text-xs text-[#64748B] mb-1">Rejection Reason</label>
                  <input type="text" value={form.rejection_reason} onChange={e => setForm(p => ({ ...p, rejection_reason: e.target.value }))} placeholder="Describe rejection reason..." className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703]" />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 border border-[#E2E8F0] rounded text-sm text-[#64748B] hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={saving} className="bg-[#FFB703] text-[#0A1128] font-semibold px-5 py-2 rounded text-sm hover:bg-[#E8A200] transition-colors disabled:opacity-60" data-testid="save-mrn-btn">
                {saving ? "Saving..." : "Record MRN"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {/* Document Upload Dialog */}
      <Dialog open={!!uploadMrn} onOpenChange={() => setUploadMrn(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-[#0A1128] text-sm">
              Attach Document to {uploadMrn?.mrn_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-xs text-[#64748B] mb-1">Document Type</label>
              <select value={uploadDocType} onChange={e => setUploadDocType(e.target.value)}
                className="w-full border border-[#E2E8F0] rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFB703] bg-white">
                {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-[#64748B] mb-1">Select File</label>
              <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls"
                className="block w-full text-xs text-[#64748B] file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-[#FFB703] file:text-[#0A1128] hover:file:bg-[#E8A200] cursor-pointer" />
            </div>
            <p className="text-[10px] text-[#94A3B8]">File will be uploaded and saved to the project's Document Vault (Procurement folder).</p>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setUploadMrn(null)} className="px-4 py-2 border border-[#E2E8F0] rounded text-xs text-[#64748B] hover:bg-slate-50">Cancel</button>
            <button disabled={uploading}
              onClick={() => fileRef.current?.files[0] && uploadDocument(fileRef.current.files[0])}
              className="bg-[#FFB703] text-[#0A1128] font-semibold px-4 py-2 rounded text-xs hover:bg-[#E8A200] transition-colors disabled:opacity-60 flex items-center gap-1.5"
              data-testid="upload-mrn-doc-submit">
              <Upload size={12} /> {uploading ? "Uploading..." : "Upload & Save"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
